import { useState } from "react";
import { Share, Copy, Check, X } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Discreet "Share" button for a project. On first click, asks the backend to mint
 * (or return) a public share token, then shows a tiny popover with the URL +
 * a preview of the OG card so the user knows what it looks like in DMs/email.
 */
export function ShareProjectButton({ projectId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {share_url, og_image_url}
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const openAndEnable = async () => {
    setOpen(true);
    if (data || loading) return;
    setLoading(true);
    try {
      const { data: payload } = await api.post(`/projects/${projectId}/share`);
      setData(payload);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't enable sharing");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      window.prompt("Copy this link:", data.share_url);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke the share link? Existing posts will stop showing the preview.")) return;
    setRevoking(true);
    try {
      await api.delete(`/projects/${projectId}/share`);
      toast.success("Share link revoked");
      setData(null);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={openAndEnable}
        data-testid="share-project-btn"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#a3a3a3] hover:text-white border border-[#262626] hover:border-[#FF2D2D] px-3 py-1.5 transition-colors"
      >
        <Share size={12} weight="bold" /> Share
      </button>

      {open && (
        <>
          {/* click-outside scrim */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-50 w-[320px] sm:w-[400px] bg-[#0a0a0a] border border-[#262626] p-4 shadow-2xl"
            data-testid="share-popover"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#FF2D2D]">Public share link</div>
                <div className="text-xs text-[#a3a3a3] mt-1">
                  Anyone with this link sees a preview card. Opening it still requires login.
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#a3a3a3] hover:text-white" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {loading || !data ? (
              <div className="text-xs text-[#a3a3a3] py-4 text-center">Preparing link…</div>
            ) : (
              <>
                <div className="border border-[#262626] bg-[#050505]" data-testid="share-og-preview">
                  <img
                    src={data.og_image_url}
                    alt="Share preview"
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-stretch gap-2 mt-3">
                  <input
                    type="text"
                    value={data.share_url}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    data-testid="share-url-input"
                    className="flex-1 bg-[#050505] border border-[#262626] px-2.5 py-2 text-xs text-white truncate focus:border-[#FF2D2D] outline-none"
                  />
                  <button
                    type="button"
                    onClick={copy}
                    data-testid="share-copy-btn"
                    className="btn-primary px-3 py-2 text-[11px] uppercase tracking-wider flex items-center gap-1.5"
                  >
                    {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={revoke}
                  disabled={revoking}
                  data-testid="share-revoke-btn"
                  className="mt-3 text-[10px] uppercase tracking-wider text-[#a3a3a3] hover:text-[#FF2D2D] disabled:opacity-50"
                >
                  {revoking ? "Revoking…" : "Revoke link"}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
